import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Switch,
  TextField,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Tooltip,
  IconButton,
  FormHelperText,
  Divider,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  InputAdornment,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction
} from '@mui/material';
import FieldSelector from './FieldSelector';
import {
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  Sync as SyncIcon,
  Lock as LockIcon,
  Queue as QueueIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  History as HistoryIcon
} from '@mui/icons-material';

const WorkflowExecutionSettings = ({ 
  executionControl = {}, 
  onChange, 
  errors = {},
  disabled = false,
  showAdvanced = true 
}) => {
  const [localSettings, setLocalSettings] = useState({
    mode: 'on_change',
    fieldTracking: {
      enabled: true,
      trackedFields: [],
      ignoreFields: []
    },
    deduplication: {
      enabled: true,
      window: 5000,
      strategy: 'exact',
      customHash: ''
    },
    cascadePrevention: {
      enabled: true,
      maxDepth: 5,
      preventSelfTrigger: true,
      preventCircular: true,
      allowedSources: []
    },
    cooldown: {
      enabled: false,
      period: 10000,
      perEntity: true
    },
    executionWindow: {
      enabled: false,
      startTime: '00:00',
      endTime: '23:59',
      timezone: 'UTC',
      daysOfWeek: [],
      skipHolidays: false
    },
    batching: {
      enabled: false,
      maxBatchSize: 10,
      batchWindow: 1000,
      processingStrategy: 'sequential'
    },
    priority: {
      level: 5,
      allowPreemption: false,
      queueStrategy: 'fifo'
    },
    entityLocking: {
      enabled: false,
      timeout: 30000,
      strategy: 'optimistic'
    },
    ...executionControl
  });

  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    deduplication: false,
    cascade: false,
    timing: false,
    advanced: false
  });

  useEffect(() => {
    setLocalSettings(prev => ({
      ...prev,
      ...executionControl
    }));
  }, [executionControl]);

  const handleChange = (path, value) => {
    const newSettings = { ...localSettings };
    const pathParts = path.split('.');
    let current = newSettings;
    
    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!current[pathParts[i]]) {
        current[pathParts[i]] = {};
      }
      current = current[pathParts[i]];
    }
    
    current[pathParts[pathParts.length - 1]] = value;
    
    setLocalSettings(newSettings);
    if (onChange) {
      onChange(newSettings);
    }
  };

  const handleExpandSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const executionModes = [
    { 
      value: 'on_change', 
      label: 'On Change', 
      description: 'Execute only when tracked fields change',
      icon: <SyncIcon />
    },
    { 
      value: 'once_per_event', 
      label: 'Once Per Event', 
      description: 'Execute once for each unique event',
      icon: <PlayArrowIcon />
    },
    { 
      value: 'once_per_entity', 
      label: 'Once Per Entity', 
      description: 'Execute only once per entity lifetime',
      icon: <LockIcon />
    },
    { 
      value: 'always', 
      label: 'Always', 
      description: 'Execute every time (with safety checks)',
      icon: <SpeedIcon />
    }
  ];

  const daysOfWeek = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' }
  ];

  const triggerSources = [
    { value: 'user', label: 'User Actions' },
    { value: 'api', label: 'API Calls' },
    { value: 'automation', label: 'Other Automations' },
    { value: 'system', label: 'System Events' },
    { value: 'scheduled', label: 'Scheduled Tasks' }
  ];

  return (
    <Box sx={{ width: '100%' }}>
      {/* Basic Settings */}
      <Accordion 
        expanded={expandedSections.basic}
        onChange={() => handleExpandSection('basic')}
        disabled={disabled}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SpeedIcon color="primary" />
            <Typography variant="h6">Execution Mode</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormControl fullWidth margin="normal">
            <FormLabel>Execution Mode</FormLabel>
            <Select
              value={localSettings.mode}
              onChange={(e) => handleChange('mode', e.target.value)}
              error={!!errors.mode}
            >
              {executionModes.map(mode => (
                <MenuItem key={mode.value} value={mode.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {mode.icon}
                    <Box>
                      <Typography variant="body1">{mode.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {mode.description}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {errors.mode && (
              <FormHelperText error>{errors.mode}</FormHelperText>
            )}
          </FormControl>

          {/* Field Tracking (for on_change mode) */}
          {localSettings.mode === 'on_change' && (
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSettings.fieldTracking.enabled}
                    onChange={(e) => handleChange('fieldTracking.enabled', e.target.checked)}
                  />
                }
                label="Enable Field Tracking"
              />
              
              {localSettings.fieldTracking.enabled && (
                <Box sx={{ mt: 2 }}>
                  <FieldSelector
                    entityType="task"
                    selectedFields={localSettings.fieldTracking.trackedFields || []}
                    onChange={(fields) => handleChange('fieldTracking.trackedFields', fields)}
                    label="Tracked Fields"
                    helperText="Leave empty to track all fields. Only changes to these fields will trigger the workflow."
                    disabled={disabled}
                    multiple={true}
                    variant="chips"
                  />
                  
                  <Box sx={{ mt: 3 }}>
                    <FieldSelector
                      entityType="task"
                      selectedFields={localSettings.fieldTracking.ignoreFields || []}
                      onChange={(fields) => handleChange('fieldTracking.ignoreFields', fields)}
                      label="Ignored Fields"
                      helperText="Changes to these fields will NOT trigger the workflow, even if tracked."
                      disabled={disabled}
                      multiple={true}
                      variant="chips"
                    />
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Deduplication Settings */}
      <Accordion 
        expanded={expandedSections.deduplication}
        onChange={() => handleExpandSection('deduplication')}
        disabled={disabled}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon color="primary" />
            <Typography variant="h6">Deduplication</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Switch
                checked={localSettings.deduplication.enabled}
                onChange={(e) => handleChange('deduplication.enabled', e.target.checked)}
              />
            }
            label="Enable Deduplication"
          />
          
          {localSettings.deduplication.enabled && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Deduplication Window (ms)"
                    value={localSettings.deduplication.window}
                    onChange={(e) => handleChange('deduplication.window', parseInt(e.target.value))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">ms</InputAdornment>
                    }}
                    helperText="Time window to check for duplicates"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <FormLabel>Strategy</FormLabel>
                    <Select
                      value={localSettings.deduplication.strategy}
                      onChange={(e) => handleChange('deduplication.strategy', e.target.value)}
                    >
                      <MenuItem value="exact">Exact Match</MenuItem>
                      <MenuItem value="field_based">Field Based</MenuItem>
                      <MenuItem value="custom">Custom Hash</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              
              {localSettings.deduplication.strategy === 'custom' && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Custom Hash Function"
                  value={localSettings.deduplication.customHash}
                  onChange={(e) => handleChange('deduplication.customHash', e.target.value)}
                  helperText="JavaScript function to generate deduplication hash"
                  sx={{ mt: 2 }}
                />
              )}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Cascade Prevention */}
      <Accordion 
        expanded={expandedSections.cascade}
        onChange={() => handleExpandSection('cascade')}
        disabled={disabled}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">Cascade Prevention</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <FormControlLabel
            control={
              <Switch
                checked={localSettings.cascadePrevention.enabled}
                onChange={(e) => handleChange('cascadePrevention.enabled', e.target.checked)}
              />
            }
            label="Enable Cascade Prevention"
          />
          
          {localSettings.cascadePrevention.enabled && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Chain Depth"
                    value={localSettings.cascadePrevention.maxDepth}
                    onChange={(e) => handleChange('cascadePrevention.maxDepth', parseInt(e.target.value))}
                    inputProps={{ min: 1, max: 20 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={localSettings.cascadePrevention.preventSelfTrigger}
                          onChange={(e) => handleChange('cascadePrevention.preventSelfTrigger', e.target.checked)}
                        />
                      }
                      label="Prevent Self-Trigger"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={localSettings.cascadePrevention.preventCircular}
                          onChange={(e) => handleChange('cascadePrevention.preventCircular', e.target.checked)}
                        />
                      }
                      label="Prevent Circular References"
                    />
                  </FormGroup>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Allowed Trigger Sources
                  </Typography>
                  <FormGroup row>
                    {triggerSources.map(source => (
                      <FormControlLabel
                        key={source.value}
                        control={
                          <Checkbox
                            checked={localSettings.cascadePrevention.allowedSources?.includes(source.value)}
                            onChange={(e) => {
                              const sources = localSettings.cascadePrevention.allowedSources || [];
                              if (e.target.checked) {
                                handleChange('cascadePrevention.allowedSources', [...sources, source.value]);
                              } else {
                                handleChange('cascadePrevention.allowedSources', sources.filter(s => s !== source.value));
                              }
                            }}
                          />
                        }
                        label={source.label}
                      />
                    ))}
                  </FormGroup>
                  {localSettings.cascadePrevention.allowedSources?.length === 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No source restrictions - workflow can be triggered by any source
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Timing Controls */}
      <Accordion 
        expanded={expandedSections.timing}
        onChange={() => handleExpandSection('timing')}
        disabled={disabled}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">Timing Controls</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {/* Cooldown */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={localSettings.cooldown.enabled}
                  onChange={(e) => handleChange('cooldown.enabled', e.target.checked)}
                />
              }
              label="Enable Cooldown Period"
            />
            
            {localSettings.cooldown.enabled && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Cooldown Period"
                    value={localSettings.cooldown.period}
                    onChange={(e) => handleChange('cooldown.period', parseInt(e.target.value))}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">ms</InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={localSettings.cooldown.perEntity}
                        onChange={(e) => handleChange('cooldown.perEntity', e.target.checked)}
                      />
                    }
                    label="Per Entity Cooldown"
                  />
                </Grid>
              </Grid>
            )}
          </Box>

          <Divider />

          {/* Execution Window */}
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={localSettings.executionWindow.enabled}
                  onChange={(e) => handleChange('executionWindow.enabled', e.target.checked)}
                />
              }
              label="Enable Execution Window"
            />
            
            {localSettings.executionWindow.enabled && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="time"
                    label="Start Time"
                    value={localSettings.executionWindow.startTime}
                    onChange={(e) => handleChange('executionWindow.startTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    type="time"
                    label="End Time"
                    value={localSettings.executionWindow.endTime}
                    onChange={(e) => handleChange('executionWindow.endTime', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Timezone"
                    value={localSettings.executionWindow.timezone}
                    onChange={(e) => handleChange('executionWindow.timezone', e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Days of Week
                  </Typography>
                  <FormGroup row>
                    {daysOfWeek.map(day => (
                      <FormControlLabel
                        key={day.value}
                        control={
                          <Checkbox
                            checked={localSettings.executionWindow.daysOfWeek?.includes(day.value)}
                            onChange={(e) => {
                              const days = localSettings.executionWindow.daysOfWeek || [];
                              if (e.target.checked) {
                                handleChange('executionWindow.daysOfWeek', [...days, day.value]);
                              } else {
                                handleChange('executionWindow.daysOfWeek', days.filter(d => d !== day.value));
                              }
                            }}
                          />
                        }
                        label={day.label}
                      />
                    ))}
                  </FormGroup>
                </Grid>
              </Grid>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Advanced Settings */}
      {showAdvanced && (
        <Accordion 
          expanded={expandedSections.advanced}
          onChange={() => handleExpandSection('advanced')}
          disabled={disabled}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <QueueIcon color="primary" />
              <Typography variant="h6">Advanced Settings</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {/* Batching */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSettings.batching.enabled}
                    onChange={(e) => handleChange('batching.enabled', e.target.checked)}
                  />
                }
                label="Enable Batching"
              />
              
              {localSettings.batching.enabled && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Max Batch Size"
                      value={localSettings.batching.maxBatchSize}
                      onChange={(e) => handleChange('batching.maxBatchSize', parseInt(e.target.value))}
                      inputProps={{ min: 1, max: 100 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Batch Window"
                      value={localSettings.batching.batchWindow}
                      onChange={(e) => handleChange('batching.batchWindow', parseInt(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">ms</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <FormLabel>Processing Strategy</FormLabel>
                      <Select
                        value={localSettings.batching.processingStrategy}
                        onChange={(e) => handleChange('batching.processingStrategy', e.target.value)}
                      >
                        <MenuItem value="parallel">Parallel</MenuItem>
                        <MenuItem value="sequential">Sequential</MenuItem>
                        <MenuItem value="priority">Priority Based</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Box>

            <Divider />

            {/* Priority */}
            <Box sx={{ mt: 3, mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Execution Priority
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography gutterBottom>Priority Level: {localSettings.priority.level}</Typography>
                  <Slider
                    value={localSettings.priority.level}
                    onChange={(e, value) => handleChange('priority.level', value)}
                    min={1}
                    max={10}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <FormLabel>Queue Strategy</FormLabel>
                    <Select
                      value={localSettings.priority.queueStrategy}
                      onChange={(e) => handleChange('priority.queueStrategy', e.target.value)}
                    >
                      <MenuItem value="fifo">First In First Out</MenuItem>
                      <MenuItem value="lifo">Last In First Out</MenuItem>
                      <MenuItem value="priority">Priority Based</MenuItem>
                      <MenuItem value="random">Random</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* Entity Locking */}
            <Box sx={{ mt: 3 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={localSettings.entityLocking.enabled}
                    onChange={(e) => handleChange('entityLocking.enabled', e.target.checked)}
                  />
                }
                label="Enable Entity Locking"
              />
              
              {localSettings.entityLocking.enabled && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Lock Timeout"
                      value={localSettings.entityLocking.timeout}
                      onChange={(e) => handleChange('entityLocking.timeout', parseInt(e.target.value))}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">ms</InputAdornment>
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <FormLabel>Locking Strategy</FormLabel>
                      <Select
                        value={localSettings.entityLocking.strategy}
                        onChange={(e) => handleChange('entityLocking.strategy', e.target.value)}
                      >
                        <MenuItem value="optimistic">Optimistic</MenuItem>
                        <MenuItem value="pessimistic">Pessimistic</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Summary Alert */}
      <Alert 
        severity="info" 
        sx={{ mt: 2 }}
        icon={<InfoIcon />}
      >
        <Typography variant="subtitle2" gutterBottom>
          Current Configuration Summary:
        </Typography>
        <Typography variant="body2">
          • Mode: {executionModes.find(m => m.value === localSettings.mode)?.label}
          {localSettings.deduplication.enabled && ` • Deduplication: ${localSettings.deduplication.window}ms window`}
          {localSettings.cooldown.enabled && ` • Cooldown: ${localSettings.cooldown.period}ms`}
          {localSettings.cascadePrevention.enabled && ` • Max depth: ${localSettings.cascadePrevention.maxDepth}`}
        </Typography>
      </Alert>
    </Box>
  );
};

export default WorkflowExecutionSettings;